package features.home.ui.components

import kotlinx.html.*
import kotlinx.html.js.onClickFunction
import AppState
import features.attendance.helpers.AttendanceHelpers
import features.members.domain.Member

object AdminScheduleCard {
    fun render(parent: DIV, teamId: String) {
        val team = AppState.teams.find { it.id == teamId } ?: return
        val scheduledUsers = AttendanceHelpers.getAllUsersScheduledToday(teamId)
        
        // Calculate statistics
        val totalUsers = scheduledUsers.size
        val clockedInCount = scheduledUsers.count { member ->
            val status = AttendanceHelpers.getAttendanceStatus(member.id, teamId)
            status == "clocked_in" || status == "clocked_out"
        }
        val absentCount = scheduledUsers.count { member ->
            val status = AttendanceHelpers.getAttendanceStatus(member.id, teamId)
            status == "not_clocked_in"
        }
        
        parent.div("bg-gradient-to-br from-violet-500/20 to-brand-cyan/20 p-6 rounded-2xl border border-violet-500/30 mb-8 relative overflow-hidden") {
            // Header
            div("flex justify-between items-start mb-4") {
                div {
                    h3("text-xl font-bold mb-1") { +"Today's Schedule - ${team.name}" }
                    p("text-slate-400 text-sm") {
                        val todayDate = AttendanceHelpers.getTodayDate()
                        +todayDate
                    }
                }
                
                // Stats
                div("flex gap-3") {
                    span("px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-400") {
                        +"✓ $clockedInCount Clocked In"
                    }
                    span("px-3 py-1 rounded-full text-xs font-bold bg-slate-500/20 text-slate-400") {
                        +"⏳ $absentCount Pending"
                    }
                }
            }
            
            // User List
            if (scheduledUsers.isEmpty()) {
                div("text-center py-8 bg-dark-800/50 rounded-xl border border-dark-700") {
                    p("text-slate-400") { +"No team members scheduled for today" }
                }
            } else {
                div("bg-dark-800/50 rounded-xl border border-dark-700 overflow-hidden") {
                    // Table Header
                    div("grid grid-cols-12 gap-4 p-4 bg-dark-900/50 border-b border-dark-700 text-slate-400 text-sm font-medium") {
                        div("col-span-4") { +"Member" }
                        div("col-span-3") { +"Work Hours" }
                        div("col-span-2") { +"Clock In" }
                        div("col-span-2") { +"Clock Out" }
                        div("col-span-1") { +"Status" }
                    }
                    
                    // User Rows
                    div("max-h-96 overflow-y-auto") {
                        scheduledUsers.forEach { member ->
                            renderUserRow(this, member, teamId)
                        }
                    }
                }
            }
            
            // Action Button
            div("mt-4 flex justify-end") {
                button(classes = "bg-dark-700 hover:bg-dark-600 text-white px-6 py-2 rounded-lg font-medium transition border border-slate-600") {
                    onClickFunction = {
                        // Navigate to team detail attendance tab
                        AppState.currentPage = "team-detail"
                        AppState.currentTeam = team
                        AppState.currentTab = "attendance"
                        Router.render()
                    }
                    +"View Full Attendance"
                }
            }
        }
    }
    
    private fun renderUserRow(parent: DIV, member: Member, teamId: String) {
        val (startTime, endTime) = AttendanceHelpers.getWorkHoursForUser(member.id, teamId)
        val shift = AttendanceHelpers.getUserShiftForToday(member.id, teamId)
        val attendanceStatus = AttendanceHelpers.getAttendanceStatus(member.id, teamId)
        val todayRecord = AttendanceHelpers.getTodayAttendance(member.id, teamId)
        val isLate = AttendanceHelpers.isLate(member.id, teamId)
        
        parent.div("grid grid-cols-12 gap-4 p-4 border-b border-dark-700 last:border-0 hover:bg-dark-700/30 transition") {
            // Member Info
            div("col-span-4 flex items-center gap-3") {
                div("w-10 h-10 rounded-full bg-gradient-to-br from-brand-cyan to-violet-500 flex items-center justify-center text-white font-bold") {
                    +(member.name.firstOrNull()?.toString()?.uppercase() ?: "?")
                }
                div {
                    p("font-medium text-white") { +member.name }
                    p("text-xs text-slate-400") {
                        if (shift != null && shift.code != "OFF") {
                            +"Shift: ${shift.code}"
                        } else {
                            +"Default Hours"
                        }
                    }
                }
            }
            
            // Work Hours
            div("col-span-3 flex items-center") {
                p("text-sm text-slate-300") { +"$startTime - $endTime" }
            }
            
            // Clock In
            div("col-span-2 flex items-center") {
                p("text-sm font-medium ${if (isLate) "text-amber-400" else "text-slate-300"}") {
                    if (todayRecord?.checkIn != null && todayRecord.checkIn != "-") {
                        +todayRecord.checkIn
                    } else {
                        span("text-slate-500") { +"--:--" }
                    }
                }
            }
            
            // Clock Out
            div("col-span-2 flex items-center") {
                p("text-sm font-medium text-slate-300") {
                    if (todayRecord?.checkOut != null && todayRecord.checkOut != "-") {
                        +todayRecord.checkOut
                    } else {
                        span("text-slate-500") { +"--:--" }
                    }
                }
            }
            
            // Status
            div("col-span-1 flex items-center") {
                val (icon, color) = when (attendanceStatus) {
                    "clocked_out" -> Pair("✅", "text-emerald-400")
                    "clocked_in" -> Pair("🔵", "text-blue-400")
                    else -> Pair("⏳", "text-slate-500")
                }
                span("text-lg $color") { +icon }
            }
        }
    }
}
